import { useVerify } from '@entities/Authentication/model.ts';
import { useCheckPasswordMutation } from '@entities/Authentication/rtk-query.ts';
import { newPasswordSchema } from '@entities/Authentication/schemas.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import YupPassword from 'yup-password';
import { useEditorClientSharedState } from '@pages/personal/client/editor-page/model.ts';
import { doTransformRTKErrorResponse } from '@shared/helpers';
import { useAppDispatch, usePlatformSettings } from '@shared/hooks';
import { type RegistrationMethod } from '@shared/types/common';
import { usePhoneVerificationIncomingModal } from '@shared/ui/modals/phone-verification-incoming-modal/helpers.ts';
import { useRegistrationRedirect } from '@src/library/hooks/user/helpers.ts';
import { closeAuthModal, openDialogModal, toggleFrameModal } from 'library/redux/modal';
import type { Register } from 'library/types/auth';
import { useAuth } from '../../AuthContext';

YupPassword(yup);

const phoneCode = yup.object({
  phonenumber: yup.string().required('Это поле обязательно').min(6, 'Номер слишком короткий'),
  acceptAgreement: yup
    .bool()
    .oneOf([true], 'Необходимо принять условия')
    .required('Необходимо принять условия'),
  acceptPersonalData: yup
    .bool()
    .oneOf([true], 'Необходимо принять условия')
    .required('Необходимо принять условия'),
  acceptAds: yup
    .bool()
    .oneOf([true], 'Необходимо принять условия')
    .required('Необходимо принять условия'),
});

const schemaCode = yup
  .object({
    passcode: yup.string().required('Это поле обязательно').min(4, 'Минимум 4 символа'),
  })
  .required();

const schemaPassword = newPasswordSchema;
// yup
//   .object({
//     password: yup
//       .string()
//       .required('Заполните оба поля')
//       .min(8, 'Пароль не может быть короче 8 символов')
//       .minLowercase(1, 'Пароль должен содержать хотя бы 1 строчная буква')
//       .minUppercase(1, 'Пароль должен содержать хотя бы 1 заглавная буква')
//       .minNumbers(1, 'Пароль должен содержать хотя бы 1 цифру')
//       .minSymbols(1, 'Пароль должен содержать хотя бы 1 специальный симвл'),
//
//     passwordRepeat: yup
//       .string()
//       .required('Заполните оба поля')
//       .oneOf([yup.ref('password')], 'Пароли не совпадают'),
//   })
//   .required();

// First Step
type FirstStepForm = {
  phonenumber: string;
  acceptAgreement: boolean;
  acceptPersonalData: boolean;
  acceptAds: boolean;
};

export const useRegister = (openRestore: () => void) => {
  const authContext = useAuth();
  const dispatch = useAppDispatch();

  const { updateEditorClientSharedState } = useEditorClientSharedState();
  const { doRedirectWithSavePageUrl } = useRegistrationRedirect();
  const { openPhoneVerificationIncomingModal } = usePhoneVerificationIncomingModal();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [phone, setPhone] = useState<string>('');
  const [passcode, setPasscode] = useState<string>('');
  const [showTimer, setShowTimer] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState<boolean>(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [checkPassword] = useCheckPasswordMutation();

  const { onCheckPhoneVerification, onPhoneVerification, isVerificationLoading } = useVerify();
  const settings = usePlatformSettings();
  const isIncomingType = settings.first_method === 'INCOMING_SMS';
  // Шаг 1: телефон
  const {
    control: controlPhoneNumber,
    handleSubmit,
    setError: setPhoneError,
    reset: resetPhoneNumber,
    formState: { errors: phoneNumberErrors, isValid: isFirstStepFormValid },
    setValue: setFirstStepValue,
  } = useForm<FirstStepForm>({
    mode: 'onChange',
    defaultValues: {
      phonenumber: authContext?.state.phone ?? '',
      acceptAgreement: false,
      acceptPersonalData: false,
      acceptAds: false,
    },
    resolver: yupResolver<FirstStepForm>(phoneCode),
  });
  const checkedPhone = async (phonenumber: string, type: RegistrationMethod) => {
    setIsLoading(true);
    await onPhoneVerification({
      type,
      phonenumber: `+${phonenumber.replace(/\+/, '')}`,
      verification_type: 'registration',
      captcha: captchaToken || undefined,
      onError: (errMessage) => {
        if (errMessage === 'User already exists') {
          dispatch(
            openDialogModal({
              title: 'Регистрация',
              text: 'Данный номер уже есть в системе. Восстановить пароль?',
              confirmText: 'Да, восстановить',
              confirm: openRestore,
              declineText: 'Отмена',
              decline: () => resetPhoneNumber({ phonenumber: '+7' }),
            })
          );
        } else {
          setPhoneError('phonenumber', {
            type: 'value',
            message: errMessage,
          });
        }
      },
      onSuccess: (data) => {
        if (isIncomingType) {
          openPhoneVerificationIncomingModal({
            disableOverlayCss: true,
            onSuccess: () => {
              setPhone(phonenumber);
              setStep(3);
              data?.passcode && setPasscode(data.passcode);
              authContext.dispatch({ type: 'head', payload: 'Создание пароля' });
            },
            passcode: data?.passcode,
            phonenumber: phonenumber,
            number: data?.number,
          });
        } else {
          setStep(2);
          setShowTimer(true);
          setPhone(phonenumber);
          authContext.dispatch({ type: 'head', payload: 'Подтверждение регистрации' });
        }
      },
    });
    setIsLoading(false);
  };

  const timerAction = () => {
    setShowTimer(false);
  };

  // Шаг 2: код
  const {
    control: controlPassCode,
    handleSubmit: submitCode,
    formState: { errors: passcodeErrors },
    setError,
  } = useForm<{ passcode: string }>({ resolver: yupResolver(schemaCode) });

  const checkCode = async (data: { passcode: string }) => {
    setIsLoading(true);
    await onCheckPhoneVerification({
      phonenumber: `+${phone.replace(/\+/, '')}`,
      verification_type: 'registration',
      passcode: data.passcode,
      onError: (errMessage) => {
        setError('passcode', {
          type: 'value',
          message: errMessage,
        });
      },
      onSuccess: () => {
        setPasscode(data.passcode);
        setStep(3);
      },
    });
    setIsLoading(false);
  };

  // Шаг 3: пароль
  const {
    control: passwordControl,
    handleSubmit: submitForm,
    formState: { errors: passwordErrors },
    setError: setPasswordError,
  } = useForm<{ password: string; passwordRepeat: string }>({
    resolver: yupResolver(schemaPassword),
  });

  const confirmForm = async (data: Register) => {
    setIsLoading(true);
    const response = await checkPassword({ password: data.password });
    if (response?.error) {
      const transformedResp = doTransformRTKErrorResponse(response.error as FetchBaseQueryError);
      if (transformedResp?.firstError) {
        setPasswordError('password', {
          type: 'manual',
          message: transformedResp?.firstError,
        });
      }
    } else {
      dispatch(closeAuthModal());
      updateEditorClientSharedState({
        phonenumber: phone,
        phonenumber_code: passcode,
        password: data.password,
      });
      doRedirectWithSavePageUrl('personal/edit');
    }
    setIsLoading(false);
  };

  const openAgreement = () => {
    // return window.open(`${settings.platform_domain}/media/site_files/agreement.pdf`);

    return dispatch(
      toggleFrameModal({ isOpen: true, url: `${settings.platform_domain}/agreement/` })
    );
  };

  const openPersonalDate = () => {
    // return window.open(`${settings.platform_domain}/media/site_files/agreement.pdf`);

    return dispatch(
      toggleFrameModal({ isOpen: true, url: `${settings.platform_domain}/personal/edit` })
    );
  };

  const onCaptchaChange = (token?: string) => {
    if (token) {
      setCaptchaToken(token);
      setIsCaptchaVerified(true);
    } else {
      setCaptchaToken('');
      setIsCaptchaVerified(false);
    }
  };

  return {
    isLoading,
    checkedPhone,
    controlPhoneNumber,
    controlPassCode,
    Controller,
    phoneNumberErrors,
    handleSubmit,
    step,
    setStep,
    checkCode,
    passcodeErrors,
    passwordControl,
    submitCode,
    passwordErrors,
    submitForm,
    confirmForm,
    showTimer,
    timerAction,
    phone,
    setShowPassword,
    showPassword,
    setShowPasswordRepeat,
    showPasswordRepeat,
    openAgreement,
    openPersonalDate,
    setFirstStepValue,
    isCaptchaVerified,
    setIsCaptchaVerified,
    onCaptchaChange,
    isFirstStepFormValid,
    isVerificationLoading,
  };
};
